/**
 * Solid instance geometry (`huebo:solid-geometry`):
 * - `cube-indexed` (default): the RoundedBox merged into 152 indexed vertices.
 * - `quad` (plan S10): one unit quad per exposed face, oriented in the vertex shader, with a
 *   fragment-shader bevel (solidFaceQuadMaterial.ts). Only the chunked solid renderer meshes per
 *   face; the grouped path keeps cube records and falls back to `cube-indexed`.
 */
export type SolidGeometryMode = 'cube-indexed' | 'quad'
/** Stored setting: an explicit mode, or 'auto' (quad at QUAD_AUTO_MIN_SIDE and above, else cube-indexed). */
export type SolidGeometrySetting = SolidGeometryMode | 'auto'
export const DEFAULT_SOLID_GEOMETRY_SETTING: SolidGeometrySetting = 'auto'
/** Fallback effective mode when no bounds are known (small documents, previews). */
export const DEFAULT_SOLID_GEOMETRY_MODE: SolidGeometryMode = 'cube-indexed'
/** Owner decision (2026-10-11): quads are fine at high sizes; longest side at/above this gets quads in 'auto'. */
export const QUAD_AUTO_MIN_SIDE = 128
export const SOLID_GEOMETRY_STORAGE_KEY = 'huebo:solid-geometry'
export const SOLID_GEOMETRY_CHANGE_EVENT = 'huebo:solid-geometry-change'

/**
 * Rounded-box parameters shared by the cube geometry (RoundedBoxGeometry) and the quad material,
 * which approximates the same look in the fragment shader.
 */
/** Corner radius of the rounded box, in voxel units (also the width of the bevel band on a face). */
export const SOLID_BEVEL_INSET_RATIO = 0.04
/** Segments per rounded edge of the box; the quad bevel quantises its normal tilt into this many steps. */
export const SOLID_BEVEL_SEGMENTS = 2
export const SOLID_BEVEL_DEPTH_FACTOR = 0.1
/**
 * Outward displacement of an exposed face along its normal (vertex shader of the cube material:
 * `min(SOLID_BEVEL_DEPTH_FACTOR, SOLID_BEVEL_INSET_RATIO * 0.6)` = 0.024). The quad geometry places
 * each face quad at the same offset (plan §5 Q4: "keep the 0.024 face offset for parity").
 */
export const SOLID_FACE_OFFSET = Math.min(SOLID_BEVEL_DEPTH_FACTOR, SOLID_BEVEL_INSET_RATIO * 0.6)

/**
 * `huebo:solid-drop-bottom-faces` = '1' (plan §5 Q9): in `quad` geometry, skip the y− face of
 * voxels at y = 0 (the floor is never seen from below when the camera is clamped). Default off.
 */
export const SOLID_DROP_BOTTOM_FACES_KEY = 'huebo:solid-drop-bottom-faces'
